import type { LucideIcon } from 'lucide-react';
import {
  Award,
  TrendingUp,
  Coins,
  Trophy,
  FileCheck2,
  GraduationCap,
  ShieldAlert,
  PhoneOff,
  TrendingDown,
  BookOpen,
  Clock,
  Hourglass,
  Gauge,
} from 'lucide-react';
import { ENGLISH_MESSAGES } from '@/lib/i18n/catalogues';
import { translate } from '@/lib/i18n/translate';
import { riskLevelLabel } from '@/components/user-portal/learner-i18n';

export type CelebrationTone = 'happy' | 'info';

export type CelebrationKind =
  | 'badge_earned'
  | 'level_up'
  | 'points_awarded'
  | 'rank_climbed'
  | 'certificate_received'
  | 'training_completed'
  | 'phishing_compromised'
  | 'vishing_failed'
  | 'rank_dropped'
  | 'training_assigned'
  | 'training_due_soon'
  | 'training_expired'
  | 'risk_level_increased';

interface BaseEvent {
  id: string;
  kind: CelebrationKind;
}

export type CelebrationEvent =
  | (BaseEvent & { kind: 'badge_earned'; badgeName: string; badgeIcon?: string; eventDescription?: string; tier?: 'bronze' | 'silver' | 'gold' | 'platinum' })
  | (BaseEvent & { kind: 'level_up'; levelName: string; levelIcon?: string; pointsRequired: number; percentile?: number })
  | (BaseEvent & { kind: 'points_awarded'; points: number; activity: string; totalPoints: number })
  | (BaseEvent & { kind: 'rank_climbed'; rank: number; delta: number; nextRankName?: string; gap?: number })
  | (BaseEvent & { kind: 'certificate_received'; courseTitle: string; certificateImage?: string })
  | (BaseEvent & { kind: 'training_completed'; courseTitle: string; courseImage?: string; points?: number })
  | (BaseEvent & { kind: 'phishing_compromised'; campaignName: string })
  | (BaseEvent & { kind: 'vishing_failed'; campaignName?: string })
  | (BaseEvent & { kind: 'rank_dropped'; rank: number; delta: number })
  | (BaseEvent & { kind: 'training_assigned'; courseTitle: string; courseImage?: string; dueDate: string; minutes?: number })
  | (BaseEvent & { kind: 'training_due_soon'; courseTitle: string; courseImage?: string; daysLeft: number })
  | (BaseEvent & { kind: 'training_expired'; courseTitle: string; courseImage?: string })
  | (BaseEvent & { kind: 'risk_level_increased'; level: 'High' | 'Medium' | 'Low' });

export interface CelebrationCopy {
  tone: CelebrationTone;
  headline: string;
  body: string;
  metric?: string;
  ctaLabel: string;
  ctaHref?: string;
  secondaryLabel?: string;
  icon: LucideIcon;
  accent: string;
  priority: number;
}

export const celebrationPriority: Record<CelebrationKind, number> = {
  level_up: 100,
  badge_earned: 90,
  certificate_received: 85,
  rank_climbed: 75,
  training_completed: 70,
  points_awarded: 50,
  risk_level_increased: 48,
  phishing_compromised: 45,
  vishing_failed: 44,
  training_expired: 42,
  rank_dropped: 40,
  training_due_soon: 30,
  training_assigned: 20,
};

type Translate = (key: string, params?: Record<string, string | number>) => string;

/** English, for callers outside a language context (tone and link lookups). */
const inEnglish: Translate = (key, params) => translate(ENGLISH_MESSAGES, ENGLISH_MESSAGES, key, params);

/**
 * The copy for one celebration, in the language `t` speaks (locale audit
 * 2026-09-26, L-02): from messages/portal, with ICU plurals where a count is
 * shown — Arabic has six plural forms, so the English `n === 1 ? 'day' :
 * 'days'` could never be translated. `**…**` marks what the modal bolds.
 */
export function describeCelebration(event: CelebrationEvent, t: Translate = inEnglish): CelebrationCopy {
  switch (event.kind) {
    case 'badge_earned':
      return {
        tone: 'happy',
        headline: t('portal.celebration.badge.headline'),
        body: event.eventDescription
          ? t('portal.celebration.badge.bodyWithDescription', { badge: event.badgeName, description: event.eventDescription })
          : t('portal.celebration.badge.body', { badge: event.badgeName }),
        ctaLabel: t('portal.celebration.badge.cta'),
        ctaHref: '#badges',
        icon: Award,
        accent: tierAccent(event.tier),
        priority: celebrationPriority.badge_earned,
      };
    case 'level_up':
      return {
        tone: 'happy',
        headline: t('portal.celebration.level.headline', { level: event.levelName }),
        body: event.percentile
          ? t('portal.celebration.level.bodyWithPercentile', { points: event.pointsRequired, percentile: event.percentile })
          : t('portal.celebration.level.body', { points: event.pointsRequired }),
        ctaLabel: t('portal.celebration.level.cta'),
        ctaHref: '#points',
        icon: TrendingUp,
        accent: 'from-violet-500/20 to-fuchsia-500/10 text-violet-500',
        priority: celebrationPriority.level_up,
      };
    case 'points_awarded':
      return {
        tone: 'happy',
        headline: t('portal.celebration.points.headline', { points: event.points }),
        body: t('portal.celebration.points.body', { activity: event.activity }),
        metric: t('portal.celebration.points.metric', { points: event.totalPoints }),
        ctaLabel: t('portal.celebration.points.cta'),
        icon: Coins,
        accent: 'from-amber-500/20 to-yellow-500/10 text-amber-500',
        priority: celebrationPriority.points_awarded,
      };
    case 'rank_climbed':
      return {
        tone: 'happy',
        headline: t('portal.celebration.rankUp.headline', { rank: event.rank }),
        body:
          event.nextRankName && event.gap
            ? t('portal.celebration.rankUp.bodyWithGap', { delta: event.delta, name: event.nextRankName, gap: event.gap })
            : t('portal.celebration.rankUp.body', { delta: event.delta }),
        ctaLabel: t('portal.celebration.rankUp.cta'),
        ctaHref: '#leaderboard',
        icon: Trophy,
        accent: 'from-green-500/20 to-emerald-500/10 text-green-500',
        priority: celebrationPriority.rank_climbed,
      };
    case 'certificate_received':
      return {
        tone: 'happy',
        headline: t('portal.celebration.certificate.headline'),
        body: t('portal.celebration.certificate.body', { course: event.courseTitle }),
        ctaLabel: t('portal.celebration.certificate.cta'),
        ctaHref: '#certificates',
        secondaryLabel: t('portal.celebration.later'),
        icon: FileCheck2,
        accent: 'from-blue-500/20 to-cyan-500/10 text-blue-500',
        priority: celebrationPriority.certificate_received,
      };
    case 'training_completed':
      return {
        tone: 'happy',
        headline: t('portal.celebration.trainingDone.headline'),
        body: event.points
          ? t('portal.celebration.trainingDone.bodyWithPoints', { course: event.courseTitle, points: event.points })
          : t('portal.celebration.trainingDone.body', { course: event.courseTitle }),
        ctaLabel: t('portal.celebration.trainingDone.cta'),
        ctaHref: '#trainings',
        icon: GraduationCap,
        accent: 'from-green-500/20 to-emerald-500/10 text-green-500',
        priority: celebrationPriority.training_completed,
      };
    // No streak kind: a streak is celebrated by the badge it earns — "Week
    // Warrior" at 7 days, "Month Master" at 30 — as badge_earned. The
    // streak_milestone kind here was never emitted (gamification-4 item 1).
    case 'phishing_compromised':
      return {
        tone: 'info',
        headline: t('portal.celebration.compromised.headline'),
        body: t('portal.celebration.compromised.body', {
          campaign: event.campaignName || t('portal.celebration.compromised.recentSimulation'),
        }),
        ctaLabel: t('portal.celebration.compromised.cta'),
        ctaHref: '#trainings',
        secondaryLabel: t('portal.celebration.later'),
        icon: ShieldAlert,
        accent: 'from-amber-500/20 to-orange-500/10 text-amber-500',
        priority: celebrationPriority.phishing_compromised,
      };
    case 'vishing_failed':
      return {
        tone: 'info',
        headline: t('portal.celebration.vishing.headline'),
        body: event.campaignName
          ? t('portal.celebration.vishing.bodyWithCampaign', { campaign: event.campaignName })
          : t('portal.celebration.vishing.body'),
        ctaLabel: t('portal.celebration.vishing.cta'),
        ctaHref: '#trainings',
        secondaryLabel: t('portal.celebration.later'),
        icon: PhoneOff,
        accent: 'from-amber-500/20 to-orange-500/10 text-amber-500',
        priority: celebrationPriority.vishing_failed,
      };
    case 'rank_dropped':
      return {
        tone: 'info',
        headline: t('portal.celebration.rankDown.headline'),
        body: t('portal.celebration.rankDown.body', { delta: event.delta }),
        ctaLabel: t('portal.celebration.rankDown.cta'),
        ctaHref: '#trainings',
        secondaryLabel: t('portal.celebration.later'),
        icon: TrendingDown,
        accent: 'from-slate-500/20 to-zinc-500/10 text-slate-500',
        priority: celebrationPriority.rank_dropped,
      };
    case 'training_assigned':
      return {
        tone: 'info',
        headline: t('portal.celebration.assigned.headline'),
        body: event.minutes
          ? t('portal.celebration.assigned.bodyWithMinutes', { course: event.courseTitle, date: event.dueDate, minutes: event.minutes })
          : t('portal.celebration.assigned.body', { course: event.courseTitle, date: event.dueDate }),
        ctaLabel: t('portal.celebration.assigned.cta'),
        ctaHref: '#trainings',
        secondaryLabel: t('portal.celebration.later'),
        icon: BookOpen,
        accent: 'from-blue-500/20 to-sky-500/10 text-blue-500',
        priority: celebrationPriority.training_assigned,
      };
    case 'training_due_soon':
      return {
        tone: 'info',
        headline: t('portal.celebration.dueSoon.headline', { days: event.daysLeft }),
        body: t('portal.celebration.dueSoon.body', { course: event.courseTitle }),
        ctaLabel: t('portal.celebration.dueSoon.cta'),
        ctaHref: '#trainings',
        secondaryLabel: t('portal.celebration.later'),
        icon: Clock,
        accent: 'from-amber-500/20 to-yellow-500/10 text-amber-500',
        priority: celebrationPriority.training_due_soon,
      };
    case 'training_expired':
      return {
        tone: 'info',
        headline: t('portal.celebration.expired.headline'),
        body: t('portal.celebration.expired.body', { course: event.courseTitle }),
        ctaLabel: t('portal.celebration.expired.cta'),
        ctaHref: '#trainings',
        secondaryLabel: t('portal.celebration.later'),
        icon: Hourglass,
        accent: 'from-red-500/20 to-rose-500/10 text-red-500',
        priority: celebrationPriority.training_expired,
      };
    case 'risk_level_increased':
      return {
        tone: 'info',
        headline: t('portal.celebration.risk.headline'),
        body: t('portal.celebration.risk.body', { level: riskLevelLabel(t, event.level) }),
        ctaLabel: t('portal.celebration.risk.cta'),
        ctaHref: '#trainings',
        secondaryLabel: t('portal.celebration.later'),
        icon: Gauge,
        accent: 'from-amber-500/20 to-red-500/10 text-amber-500',
        priority: celebrationPriority.risk_level_increased,
      };
  }
}

function tierAccent(tier?: 'bronze' | 'silver' | 'gold' | 'platinum'): string {
  switch (tier) {
    case 'platinum':
      return 'from-cyan-400/30 to-blue-500/10 text-cyan-400';
    case 'gold':
      return 'from-yellow-400/30 to-amber-500/10 text-yellow-500';
    case 'silver':
      return 'from-slate-300/30 to-zinc-400/10 text-slate-400';
    case 'bronze':
      return 'from-orange-400/30 to-amber-600/10 text-orange-500';
    default:
      return 'from-green-500/20 to-emerald-500/10 text-green-500';
  }
}
